/* eslint-disable jsx-a11y/anchor-is-valid */
import { Badge, Button, Card, useTheme } from "flowbite-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import { useNavigate } from "react-router";
import { contactService, companyService, dealService } from "../services/main";
import {
  HiUsers,
  HiOfficeBuilding,
  HiCurrencyDollar,
  HiTrendingUp,
  HiTrendingDown,
  HiArrowRight,
  HiClock,
  HiCheckCircle,
  HiXCircle,
  HiExclamationCircle,
  HiChartBar,
  HiChartPie,
  HiFire,
  HiSparkles,
} from "react-icons/hi";
import { FadeLoader } from "react-spinners";

const DashboardPage: FC = function () {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalContacts: 0,
    totalCompanies: 0,
    totalDeals: 0,
    totalDealValue: 0,
    recentContacts: [] as any[],
    recentDeals: [] as any[],
    dealsByStage: {} as Record<string, number>,
    contactsByStatus: {} as Record<string, number>,
    monthlyDeals: [] as number[],
  });

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [contactsRes, companiesRes, dealsRes] = await Promise.all([
        contactService.listContacts(),
        companyService.listCompanies(),
        dealService.listDeals(),
      ]);

      const contacts = contactsRes.data?.contacts || [];
      const companies = companiesRes.data?.companies || [];
      const deals = dealsRes.data?.deals || [];

      // Calculate statistics
      const totalDealValue = deals.reduce((sum: number, deal: any) => sum + (deal.amount || 0), 0);
      
      // Recent contacts (last 5)
      const recentContacts = contacts
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5);

      // Recent deals (last 5)
      const recentDeals = deals
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5);

      // Deals by stage
      const dealsByStage: Record<string, number> = {};
      deals.forEach((deal: any) => {
        const stage = deal.stage || "Unknown";
        dealsByStage[stage] = (dealsByStage[stage] || 0) + 1;
      });

      // Contacts by status
      const contactsByStatus: Record<string, number> = {};
      contacts.forEach((contact: any) => {
        const status = contact.status || "NEW";
        contactsByStatus[status] = (contactsByStatus[status] || 0) + 1;
      });

      // Monthly deals (last 6 months)
      const monthlyDeals = Array(6).fill(0);
      const now = new Date();
      deals.forEach((deal: any) => {
        if (deal.createdAt) {
          const dealDate = new Date(deal.createdAt);
          const monthsAgo = (now.getFullYear() - dealDate.getFullYear()) * 12 + (now.getMonth() - dealDate.getMonth());
          if (monthsAgo >= 0 && monthsAgo < 6) {
            monthlyDeals[5 - monthsAgo]++;
          }
        }
      });

      setStats({
        totalContacts: contacts.length,
        totalCompanies: companies.length,
        totalDeals: deals.length,
        totalDealValue,
        recentContacts,
        recentDeals,
        dealsByStage,
        contactsByStatus,
        monthlyDeals,
      });
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <NavbarSidebarLayout>
        <div className="flex items-center justify-center min-h-screen">
          <FadeLoader color="#3b82f6" />
        </div>
      </NavbarSidebarLayout>
    );
  }

  // Calculate additional stats
  const wonDeals = Object.entries(stats.dealsByStage).find(([stage]) => stage === "Won")?.[1] || 0;
  const activeDeals = stats.totalDeals - wonDeals - (Object.entries(stats.dealsByStage).find(([stage]) => stage === "Lost")?.[1] || 0);
  const winRate = stats.totalDeals > 0 ? ((wonDeals / stats.totalDeals) * 100).toFixed(1) : "0";

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6 pb-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <HiChartPie className="text-blue-600 dark:text-blue-400" />
                Dashboard
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Welcome back! Here's an overview of your CRM performance.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                color="light"
                onClick={() => fetchDashboardData()}
                className="hidden sm:flex"
              >
                <HiSparkles className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4 mb-8">
          <StatCard
            title="Total Contacts"
            value={stats.totalContacts}
            icon={HiUsers}
            color="blue"
            link="/pages/contacts"
            subtitle={`${Object.values(stats.contactsByStatus).reduce((a, b) => a + b, 0)} total`}
          />
          <StatCard
            title="Total Companies"
            value={stats.totalCompanies}
            icon={HiOfficeBuilding}
            color="green"
            link="/pages/companies"
            subtitle="Active companies"
          />
          <StatCard
            title="Total Deals"
            value={stats.totalDeals}
            icon={HiCurrencyDollar}
            color="purple"
            link="/pages/deals"
            subtitle={`${activeDeals} active, ${wonDeals} won`}
          />
          <StatCard
            title="Total Deal Value"
            value={`$${stats.totalDealValue.toLocaleString()}`}
            icon={HiTrendingUp}
            color="yellow"
            link="/pages/deals"
            subtitle={`${winRate}% win rate`}
          />
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <QuickStatCard
            title="Active Deals"
            value={activeDeals}
            icon={HiFire}
            color="orange"
            trend={stats.totalDeals > 0 ? "+" + Math.round((activeDeals / stats.totalDeals) * 100) + "%" : "0%"}
          />
          <QuickStatCard
            title="Won Deals"
            value={wonDeals}
            icon={HiCheckCircle}
            color="green"
            trend={winRate + "% win rate"}
          />
          <QuickStatCard
            title="New Contacts"
            value={stats.contactsByStatus.NEW || 0}
            icon={HiClock}
            color="blue"
            trend="This month"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
          {/* Deals Pipeline Chart */}
          <DealsPipelineChart dealsByStage={stats.dealsByStage} />
          
          {/* Contacts Status Chart */}
          <ContactsStatusChart contactsByStatus={stats.contactsByStatus} />
        </div>

        {/* Monthly Deals Trend */}
        <div className="mb-8">
          <MonthlyDealsChart monthlyDeals={stats.monthlyDeals} />
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          <RecentContacts contacts={stats.recentContacts} />
          <RecentDeals deals={stats.recentDeals} />
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

// Stat Card Component
const StatCard: FC<{
  title: string;
  value: string | number;
  icon: any;
  color: string;
  link: string;
  subtitle?: string;
}> = ({ title, value, icon: Icon, color, link, subtitle }) => {
  const navigate = useNavigate();
  const colorConfig: Record<string, { bg: string; iconBg: string; iconColor: string; gradient: string }> = {
    blue: {
      bg: "bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800",
      iconBg: "bg-blue-500",
      iconColor: "text-white",
      gradient: "from-blue-500 to-blue-600",
    },
    green: {
      bg: "bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800",
      iconBg: "bg-green-500",
      iconColor: "text-white",
      gradient: "from-green-500 to-green-600",
    },
    purple: {
      bg: "bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800",
      iconBg: "bg-purple-500",
      iconColor: "text-white",
      gradient: "from-purple-500 to-purple-600",
    },
    yellow: {
      bg: "bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900 dark:to-yellow-800",
      iconBg: "bg-yellow-500",
      iconColor: "text-white",
      gradient: "from-yellow-500 to-yellow-600",
    },
  };

  const config = colorConfig[color] || colorConfig.blue;

  return (
    <Card
      className={`cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 ${config.bg}`}
      onClick={() => navigate(link)}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        <div className={`p-4 rounded-xl ${config.iconBg} shadow-lg`}>
          <Icon className={`h-7 w-7 ${config.iconColor}`} />
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
          <span>View details</span>
          <HiArrowRight className="ml-1 h-3 w-3" />
        </div>
      </div>
    </Card>
  );
};

// Quick Stat Card Component
const QuickStatCard: FC<{
  title: string;
  value: number;
  icon: any;
  color: string;
  trend: string;
}> = ({ title, value, icon: Icon, color, trend }) => {
  const colorConfig: Record<string, { bg: string; iconBg: string; iconColor: string }> = {
    orange: {
      bg: "bg-orange-50 dark:bg-orange-900/20",
      iconBg: "bg-orange-500",
      iconColor: "text-white",
    },
    green: {
      bg: "bg-green-50 dark:bg-green-900/20",
      iconBg: "bg-green-500",
      iconColor: "text-white",
    },
    blue: {
      bg: "bg-blue-50 dark:bg-blue-900/20",
      iconBg: "bg-blue-500",
      iconColor: "text-white",
    },
  };

  const config = colorConfig[color] || colorConfig.blue;

  return (
    <Card className={`${config.bg} border-0`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{trend}</p>
        </div>
        <div className={`p-3 rounded-lg ${config.iconBg}`}>
          <Icon className={`h-5 w-5 ${config.iconColor}`} />
        </div>
      </div>
    </Card>
  );
};

// Deals Pipeline Chart
const DealsPipelineChart: FC<{ dealsByStage: Record<string, number> }> = ({ dealsByStage }) => {
  const { mode } = useTheme();
  const isDarkTheme = mode === "dark";

  const stages = Object.keys(dealsByStage);
  const values = Object.values(dealsByStage);

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "donut",
      fontFamily: "Inter, sans-serif",
      toolbar: {
        show: false,
      },
    },
    labels: stages,
    colors: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"],
    legend: {
      show: true,
      position: "bottom",
    },
    dataLabels: {
      enabled: true,
    },
    theme: {
      mode: isDarkTheme ? "dark" : "light",
    },
  };

  const series = values;

  return (
    <Card className="border-0 shadow-lg">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HiChartPie className="text-purple-600 dark:text-purple-400" />
            Deals Pipeline
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Distribution by stage</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {values.reduce((a, b) => a + b, 0)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total deals</p>
        </div>
      </div>
      {series.length > 0 ? (
        <Chart height={320} options={options} series={series} type="donut" />
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          <HiChartPie className="h-12 w-12 mb-2 opacity-50" />
          <p>No deals data available</p>
        </div>
      )}
    </Card>
  );
};

// Contacts Status Chart
const ContactsStatusChart: FC<{ contactsByStatus: Record<string, number> }> = ({ contactsByStatus }) => {
  const { mode } = useTheme();
  const isDarkTheme = mode === "dark";

  const statusLabels: Record<string, string> = {
    NEW: "New",
    CONTACTED: "Contacted",
    INVALID: "Invalid",
    FOLLOW_UP: "Follow Up",
  };

  const statuses = Object.keys(contactsByStatus);
  const values = Object.values(contactsByStatus);
  const labels = statuses.map((s) => statusLabels[s] || s);

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      fontFamily: "Inter, sans-serif",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: labels,
    },
    colors: ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"],
    theme: {
      mode: isDarkTheme ? "dark" : "light",
    },
  };

  const series = [
    {
      name: "Contacts",
      data: values,
    },
  ];

  return (
    <Card className="border-0 shadow-lg">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HiChartBar className="text-blue-600 dark:text-blue-400" />
            Contacts by Status
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Status distribution</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {values.reduce((a, b) => a + b, 0)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total contacts</p>
        </div>
      </div>
      {values.length > 0 ? (
        <Chart height={320} options={options} series={series} type="bar" />
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          <HiChartBar className="h-12 w-12 mb-2 opacity-50" />
          <p>No contacts data available</p>
        </div>
      )}
    </Card>
  );
};

// Monthly Deals Chart
const MonthlyDealsChart: FC<{ monthlyDeals: number[] }> = ({ monthlyDeals }) => {
  const { mode } = useTheme();
  const isDarkTheme = mode === "dark";

  const monthNames = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthNames.push(date.toLocaleDateString("en-US", { month: "short" }));
  }

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "area",
      fontFamily: "Inter, sans-serif",
      toolbar: {
        show: false,
      },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        opacityFrom: 0.6,
        opacityTo: 0.1,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: monthNames,
    },
    colors: ["#3b82f6"],
    theme: {
      mode: isDarkTheme ? "dark" : "light",
    },
  };

  const series = [
    {
      name: "Deals Created",
      data: monthlyDeals,
    },
  ];

  const totalDeals = monthlyDeals.reduce((a, b) => a + b, 0);
  const avgDeals = totalDeals > 0 ? (totalDeals / 6).toFixed(1) : "0";

  return (
    <Card className="border-0 shadow-lg">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HiTrendingUp className="text-green-600 dark:text-green-400" />
            Deals Trend
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Deals created over the last 6 months</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalDeals}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Avg: {avgDeals}/month</p>
        </div>
      </div>
      <Chart height={320} options={options} series={series} type="area" />
    </Card>
  );
};

// Recent Contacts Component
const RecentContacts: FC<{ contacts: any[] }> = ({ contacts }) => {
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string; icon: any }> = {
      NEW: { color: "blue", label: "New", icon: HiClock },
      CONTACTED: { color: "green", label: "Contacted", icon: HiCheckCircle },
      INVALID: { color: "red", label: "Invalid", icon: HiXCircle },
      FOLLOW_UP: { color: "yellow", label: "Follow Up", icon: HiExclamationCircle },
    };

    const config = statusConfig[status] || statusConfig.NEW;
    const Icon = config.icon;

    return (
      <Badge color={config.color as any} icon={Icon}>
        {config.label}
      </Badge>
    );
  };

  return (
    <Card className="border-0 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HiUsers className="text-blue-600 dark:text-blue-400" />
            Recent Contacts
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Latest 5 contacts added</p>
        </div>
        <Button
          size="xs"
          color="light"
          onClick={() => navigate("/pages/contacts")}
          className="hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          View All
          <HiArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-3">
        {contacts.length > 0 ? (
          contacts.map((contact: any, index: number) => (
            <div
              key={contact.id}
              className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-100 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600"
              onClick={() => navigate(`/pages/contacts`)}
            >
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-700 flex items-center justify-center shadow-md">
                    <span className="text-white font-bold text-lg">
                      {contact.firstName?.[0]?.toUpperCase() || "?"}
                    </span>
                  </div>
                  {index === 0 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {contact.firstName} {contact.lastName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {contact.company?.name || "No company"}
                  </p>
                  {contact.email && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate max-w-xs">
                      {contact.email}
                    </p>
                  )}
                </div>
              </div>
              {getStatusBadge(contact.status)}
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <HiUsers className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm">No contacts yet</p>
            <Button
              size="xs"
              color="blue"
              className="mt-4"
              onClick={() => navigate("/pages/contacts")}
            >
              Add Your First Contact
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

// Recent Deals Component
const RecentDeals: FC<{ deals: any[] }> = ({ deals }) => {
  const navigate = useNavigate();

  const getStageBadge = (stage: string) => {
    const stageColors: Record<string, string> = {
      Opportunity: "blue",
      "Proposal Sent": "yellow",
      "In Negotiation": "purple",
      Won: "green",
      Lost: "red",
      Delayed: "gray",
    };

    return (
      <Badge color={stageColors[stage] as any || "gray"}>
        {stage}
      </Badge>
    );
  };

  return (
    <Card className="border-0 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HiCurrencyDollar className="text-green-600 dark:text-green-400" />
            Recent Deals
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Latest 5 deals created</p>
        </div>
        <Button
          size="xs"
          color="light"
          onClick={() => navigate("/pages/deals")}
          className="hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          View All
          <HiArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-3">
        {deals.length > 0 ? (
          deals.map((deal: any, index: number) => (
            <div
              key={deal.id}
              className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-100 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-600"
              onClick={() => navigate(`/pages/deals`)}
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 dark:from-green-500 dark:to-green-700 flex items-center justify-center shadow-md">
                    <HiCurrencyDollar className="text-white h-6 w-6" />
                  </div>
                  {index === 0 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white dark:border-gray-800"></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {deal.dealName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {deal.company?.name || "No company"}
                  </p>
                  <p className="text-xs font-medium text-green-600 dark:text-green-400 mt-1">
                    ${deal.amount?.toLocaleString() || "0"}
                  </p>
                </div>
              </div>
              <div className="ml-2">
                {getStageBadge(deal.stage)}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <HiCurrencyDollar className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm">No deals yet</p>
            <Button
              size="xs"
              color="green"
              className="mt-4"
              onClick={() => navigate("/pages/deals")}
            >
              Create Your First Deal
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default DashboardPage;
